/**
 * Core Battleship Game Types
 *
 * This file defines all TypeScript types and interfaces for the Battleship game
 * implementation. It follows the design specified in .kiro/specs/battleship-game/design.md
 *
 * Grid convention: grid[row][col], columns A–J (index 0–9), rows 1–10 (index 0–9)
 *
 * Requirements: 1.1, 1.4, 14.1, 42.1, 42.3
 */

// ============================================================================
// Basic Battleship Types
// ============================================================================

/** Column letters A–J mapping to indices 0–9 */
export type ColumnLetter = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H' | 'I' | 'J';

/**
 * Position on the Battleship grid.
 * col: 0–9 (A–J)
 * row: 0–9 (1–10)
 */
export interface Coordinate {
  col: number;  // 0-9 (A-J)
  row: number;  // 0-9 (1-10)
}

/** Ship orientation: horizontal or vertical only — no diagonal */
export type Orientation = 'horizontal' | 'vertical';

/** Player sides in a Battleship game */
export type PlayerSide = 'player1' | 'player2';

/** Game phases: placement → attack → completed */
export type GamePhase = 'placement' | 'attack' | 'completed';

/** AI difficulty levels */
export type BattleshipDifficulty = 'easy' | 'medium' | 'hard';

// ============================================================================
// Grid Types
// ============================================================================

/** Ocean Grid cell state: empty water or occupied by a ship */
export type OceanCellState = 'empty' | 'ship';

/** Tracking Grid cell state: unknown, hit, miss, or sunk */
export type TrackingCellState = 'unknown' | 'hit' | 'miss' | 'sunk';

/** Ocean Grid: shows ship positions for the owning player. 10 rows × 10 columns */
export type OceanGrid = OceanCellState[][];

/** Tracking Grid: shows attack results against the opponent. 10 rows × 10 columns */
export type TrackingGrid = TrackingCellState[][];

// ============================================================================
// Ship Types and Placement
// ============================================================================

/** The five standard Battleship ship types */
export type ShipType = 'carrier' | 'battleship' | 'cruiser' | 'submarine' | 'destroyer';

/** Standard fleet ship sizes. Total cells: 5 + 4 + 3 + 3 + 2 = 17 */
export const SHIP_SIZES: Record<ShipType, number> = {
  carrier: 5,
  battleship: 4,
  cruiser: 3,
  submarine: 3,
  destroyer: 2,
};

/**
 * Ship placement definition: ship type, starting cell, and orientation.
 * The starting cell is the top-left cell of the ship.
 */
export interface ShipPlacement {
  shipType: ShipType;
  startCell: Coordinate;
  orientation: Orientation;
}

/** Result of a ship placement attempt */
export interface PlacementResult {
  success: boolean;
  placements?: ShipPlacement[];
  error?: string;
}

/** Validation result for a ship placement */
export interface PlacementValidation {
  valid: boolean;
  reason?: 'overlap' | 'out_of_bounds' | 'invalid_orientation' | 'already_placed';
}

// ============================================================================
// Ship and Fleet Status
// ============================================================================

/** Status of a single ship during gameplay */
export interface ShipStatus {
  shipType: ShipType;
  size: number;
  placement: ShipPlacement;
  cells: Coordinate[];
  hitCells: Coordinate[];
  isSunk: boolean;
}

/** Status of a player's entire fleet */
export interface FleetStatus {
  ships: ShipStatus[];
  totalShips: number;       // Always 5
  shipsAfloat: number;
  shipsSunk: number;
}

// ============================================================================
// Attack Types
// ============================================================================

/** An attack action: coordinate and attacker */
export interface BattleshipAttack {
  coordinate: Coordinate;
  attacker: PlayerSide;
}

/** Attack result classification */
export type AttackResultType = 'hit' | 'miss' | 'sunk';

/** Result of processing an attack */
export interface AttackResult {
  success: boolean;
  coordinate: Coordinate;
  result: AttackResultType;
  sunkShip?: ShipType;
  error?: string;
  gameOver: boolean;
  winner?: PlayerSide;
}

/** Validation result for an attack attempt */
export interface AttackValidation {
  valid: boolean;
  reason?: 'already_attacked' | 'out_of_bounds' | 'not_your_turn' | 'wrong_phase' | 'game_over';
}

/** Record of a single attack in the attack history */
export interface AttackRecord {
  moveNumber: number;
  attacker: PlayerSide;
  coordinate: Coordinate;
  result: AttackResultType;
  sunkShip?: ShipType;
  timestamp: number;
}

// ============================================================================
// Game State Types
// ============================================================================

/** Complete Battleship game state for persistence, synchronization, and serialization */
export interface BattleshipGameState {
  // Grids
  player1OceanGrid: OceanGrid;
  player2OceanGrid: OceanGrid;
  player1TrackingGrid: TrackingGrid;
  player2TrackingGrid: TrackingGrid;

  // Fleet positions
  player1Fleet: ShipPlacement[];
  player2Fleet: ShipPlacement[];

  // Fleet status (derived but cached for performance)
  player1FleetStatus: FleetStatus;
  player2FleetStatus: FleetStatus;

  // Game metadata
  gamePhase: GamePhase;
  currentTurn: PlayerSide;
  attackHistory: AttackRecord[];
  gameStatus: BattleshipGameStatus;

  // Placement readiness (for multiplayer sync)
  player1Ready: boolean;
  player2Ready: boolean;
}

/** Game status discriminated union */
export type BattleshipGameStatus =
  | { type: 'placement' }
  | { type: 'active' }
  | { type: 'win'; winner: PlayerSide }
  | { type: 'resignation'; winner: PlayerSide };

// ============================================================================
// Game Result and Statistics Types
// ============================================================================

/** Result of a completed Battleship game for statistics tracking */
export interface BattleshipGameResult {
  gameId: string;
  mode: BattleshipGameSettings['mode'];
  difficulty?: BattleshipDifficulty;
  winner?: PlayerSide;
  reason: 'all_ships_sunk' | 'resignation';
  totalAttacks: number;
  hitPercentage: number;
  shipsSunk: number;
  duration: number;           // seconds
  completedAt: number;
}

/** Statistics for a specific Battleship game category */
export interface BattleshipGameStats {
  wins: number;
  losses: number;
  winPercentage: number;
  averageHitAccuracy: number;   // percentage
  averageGameLength: number;    // total attacks
}

/** Complete Battleship statistics across all modes and difficulties */
export interface BattleshipStatistics {
  userId?: string;
  singlePlayer: {
    easy: BattleshipGameStats;
    medium: BattleshipGameStats;
    hard: BattleshipGameStats;
  };
  onlineMultiplayer: BattleshipGameStats;
  totalGames: number;
  totalPlayTime: number;        // seconds
}

// ============================================================================
// Game Settings Types
// ============================================================================

/** Game settings for mode selection. No local-multiplayer — grids must stay hidden. */
export interface BattleshipGameSettings {
  mode: 'singleplayer' | 'multiplayer-realtime';
  aiDifficulty?: BattleshipDifficulty;
  isPrivate?: boolean;
}

// ============================================================================
// Component Props Types
// ============================================================================

/** BattleshipBoard component props */
export interface BattleshipBoardProps {
  engine: any; // BattleshipEngine (avoid circular import)
  gridType: 'ocean' | 'tracking';
  playerSide: PlayerSide;
  interactive?: boolean;
  isPlayerTurn?: boolean;
  gamePhase: GamePhase;
  onCellClick?: (coordinate: Coordinate) => void;
  onShipDrop?: (placement: ShipPlacement) => void;
  highlightedCells?: Coordinate[];
}

/** BattleshipBoard internal state */
export interface BattleshipBoardState {
  hoveredCell: Coordinate | null;
  dragPreview: ShipPlacement | null;
  dragValid: boolean;
  animatingAttack?: {
    coordinate: Coordinate;
    result: 'hit' | 'miss' | 'sunk';
  };
}

/** ShipDock component props */
export interface ShipDockProps {
  ships: ShipDockItem[];
  onShipDragStart: (shipType: ShipType) => void;
  onRotate: (shipType: ShipType) => void;
  onAutoPlace: () => void;
  onReady: () => void;
  allShipsPlaced: boolean;
  isReady: boolean;
}

/** Individual ship item in the ShipDock */
export interface ShipDockItem {
  type: ShipType;
  size: number;
  placed: boolean;
  orientation: Orientation;
}

/** BattleshipGameModeSelector component props */
export interface BattleshipGameModeSelectorProps {
  onStartGame: (settings: BattleshipGameSettings) => void;
  onJoinPrivateGame: (roomCode: string) => Promise<void>;
  guestGamesRemaining?: number;
  isAuthenticated?: boolean;
  isOnline?: boolean;
}

/** FleetStatus component props */
export interface FleetStatusProps {
  playerFleet: FleetStatus;
  opponentFleet: FleetStatus;
  showOpponentShipNames: boolean;
}

/** AttackHistory component props */
export interface AttackHistoryProps {
  attacks: AttackRecord[];
  currentMoveIndex?: number;
}

/** BattleshipGame main container component props */
export interface BattleshipGameProps {
  onExit?: () => void;
  initialRoomCode?: string;
}

// ============================================================================
// Utility Constants and Functions
// ============================================================================

/** Number of rows in the Battleship grid */
export const GRID_ROWS = 10;

/** Number of columns in the Battleship grid */
export const GRID_COLS = 10;

/** Total number of cells on the grid */
export const TOTAL_CELLS = GRID_ROWS * GRID_COLS; // 100

/** Total number of ship cells in a standard fleet */
export const TOTAL_SHIP_CELLS = 17; // 5 + 4 + 3 + 3 + 2

/** Column letter to index mapping */
export const COLUMN_LETTERS: ColumnLetter[] = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];

/** Check if a coordinate is within the 10×10 grid bounds */
export function isValidCoordinate(coord: Coordinate): boolean {
  return coord.col >= 0 && coord.col < GRID_COLS && coord.row >= 0 && coord.row < GRID_ROWS;
}

/** Check if two coordinates are equal */
export function coordinatesEqual(a: Coordinate, b: Coordinate): boolean {
  return a.col === b.col && a.row === b.row;
}

/** Convert a coordinate to display string (e.g., { col: 1, row: 4 } → "B5") */
export function coordinateToString(coord: Coordinate): string {
  return `${COLUMN_LETTERS[coord.col]}${coord.row + 1}`;
}

/** Convert a display string to coordinate (e.g., "B5" → { col: 1, row: 4 }) */
export function stringToCoordinate(str: string): Coordinate | null {
  if (str.length < 2 || str.length > 3) return null;

  const colLetter = str[0].toUpperCase() as ColumnLetter;
  const colIndex = COLUMN_LETTERS.indexOf(colLetter);
  if (colIndex === -1) return null;

  const rowNum = parseInt(str.substring(1), 10);
  if (isNaN(rowNum) || rowNum < 1 || rowNum > 10) return null;

  return { col: colIndex, row: rowNum - 1 };
}

/**
 * Get all cells occupied by a ship given its placement.
 * Returns an array of coordinates for each cell the ship occupies.
 */
export function getShipCells(placement: ShipPlacement): Coordinate[] {
  const size = SHIP_SIZES[placement.shipType];
  const cells: Coordinate[] = [];

  for (let i = 0; i < size; i++) {
    if (placement.orientation === 'horizontal') {
      cells.push({ col: placement.startCell.col + i, row: placement.startCell.row });
    } else {
      cells.push({ col: placement.startCell.col, row: placement.startCell.row + i });
    }
  }

  return cells;
}
